import React, { useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import Joi from '@hapi/joi';
import { Formik } from 'formik';
import { toast } from 'react-toastify';
import jsCookie from 'js-cookie';
import { useMutation, gql } from '@apollo/client';
import { logger } from 'RootShared/utility/ErrorLogger';
import useLogOut from 'Shared/hooks/useLogOut';
import withErrorBoundary from 'Components/ErrorBoundary/ErrorBoundary';
import LoginPageView from '../LoginPageView/LoginPageView';

const mutationAuthenticate = gql`
  mutation LoginPageFormAuthenticateMutation($username: String!, $password: String!) @api(name: root) {
    authenticate(username: $username, password: $password) {
      user {
        id
        customerprofile {
          user {
            email
          }
        }
        employeeprofile {
          phone
        }
      }
    }
  }
`;

const loginValidationSchema = Joi.object().keys({
  username: Joi.string().error(() => "Username field can't be empty"),
  password: Joi.string().error(() => "Password field can't be empty"),
});

const LoginPageForm = () => {
  const [logOut] = useLogOut();

  const [authenticate] = useMutation(mutationAuthenticate, {
    onCompleted(data) {
      onLoginSuccess(data);
    },
    onError(error) {
      onLoginError(error);
    },
  });

  const initialValues = {
    username: '',
    password: '',
  };

  const toastId = useRef(null);
  const history = useHistory();

  const showNotification = useCallback(
    () =>
      (toastId.current = toast('In progress, please wait...', {
        autoClose: true,
        closeButton: false,
        hideProgressBar: true,
      })),
    []
  );

  const onLoginSuccess = useCallback(
    (data) => {
      if (!data.errors && data.authenticate.user) {
        toast.update(toastId.current, {
          render: 'Logged in successfully',
          type: toast.TYPE.SUCCESS,
          autoClose: 3000,
          closeButton: true,
          hideProgressBar: false,
        });

        jsCookie.set('isAuthenticated', true);

        const initialPathName = sessionStorage.getItem('initialPathName');
        const { customerprofile, employeeprofile } = data.authenticate.user;

        if (customerprofile === null && employeeprofile === null) {
          const userType = localStorage.getItem('userType') || 'employee';
          const message = `User doesn't have ${userType} profile`;

          logOut();

          toast(message, {
            autoClose: 3000,
            closeButton: true,
            type: toast.TYPE.ERROR,
          });
        } else if (initialPathName && !initialPathName.includes('/login/') && initialPathName !== '/') {
          history.replace(initialPathName);
        } else {
          history.replace('/');
        }

        sessionStorage.removeItem('initialPathName');
      } else {
        toast.update(toastId.current, {
          render: data.errors[0] || 'Authentication error',
          type: toast.TYPE.ERROR,
          autoClose: 2000,
          closeButton: true,
          hideProgressBar: false,
        });
      }
    },
    [history, logOut]
  );

  const onLoginError = useCallback(
    (error) => {
      logger.error(error);
      toast.update(toastId.current, {
        render: 'Authentication error',
        type: toast.TYPE.ERROR,
        autoClose: 3000,
        closeButton: true,
        hideProgressBar: false,
      });
      return error;
    },
    [toastId]
  );

  const handleLoginSubmit = useCallback(
    (values, { setSubmitting, setValues }) => {
      showNotification();

      authenticate({ variables: values });

      setSubmitting(false);
      setValues(initialValues);
    },
    [authenticate, initialValues, showNotification]
  );

  const handleLoginValidation = useCallback(async (values) => {
    const validationResult = await Joi.validate(
      {
        ...values,
      },
      loginValidationSchema,
      {
        abortEarly: false,
      }
    );

    return validationResult.error;
  }, []);

  const renderFunction = useCallback((formikProps) => <LoginPageView formikProps={formikProps} />, []);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleLoginSubmit}
      validate={handleLoginValidation}
      validateOnChange={false}
      validateOnBlur={true}
      render={renderFunction}
    />
  );
};

export default withErrorBoundary(LoginPageForm);
